import { useMemo } from "react"
import { IMask } from "react-imask"
import { P, match } from "ts-pattern"

import { cn } from "@/lib/utils"
import { Column, ColumnFiltersState } from "@tanstack/react-table"

import { DropdownMenu, DropdownMenuContent } from "../../DropdownMenu"
import { LE_TableInstance } from "../types"
import { BooleanFilter } from "./BooleanFilter"
import { DateRangeFilter } from "./DateRangeFilter"
import { FilterPill } from "./FilterPill"
import { MultiEnumFilter } from "./MultiEnumFilter"
import { NumberRangeFilter } from "./NumberRangeFilter"
import { SingleEnumFilter } from "./SingleEnumFilter"

const FILTER_PILL_ID_SUFFIX = "filter-pill"
const FILTER_PILL_POPOVER_ID_SUFFIX = "filter-pill-popover"

export function ActiveFiltersList<T extends Record<string, any> = object>({
  table,
  columnFilters,
  flatColumns,
  className,
}: {
  table: LE_TableInstance<T>
  columnFilters: ColumnFiltersState
  flatColumns?: ReturnType<LE_TableInstance<T>["getAllFlatColumns"]>
  className?: string
}) {
  const columns = flatColumns ?? table.getAllFlatColumns()

  const filterColumns = useMemo(() => {
    return columns.filter(
      (col) =>
        col.columns.length === 0 && shouldShowColumnFilterPill(col, columnFilters),
    )
  }, [columns, columnFilters])

  if ((filterColumns ?? []).length === 0) return null

  return (
    <div className={cn("flex gap-2", className)}>
      {filterColumns.map((column) => {
        const label = column.columnDef.meta?.textLabel ?? column.columnDef.header
        if (typeof label !== "string") {
          throw new Error(
            "column `header` must be a string, or `meta.textLabel` must be set.",
          )
        }

        const meta = column.columnDef.meta
        const filterOptions = meta?.filterOptions
        const filterValue = columnFilters.find((f) => f.id === column.id)?.value

        const valueLabel = match(column.columnDef.filterFn)
          .with("multiEnum", () => {
            const values = (filterValue ?? []) as string[]
            return filterOptions
              ? values.map((v) => filterOptions[v]).join(", ")
              : values.join(", ")
          })
          .with("singleEnum", () => {
            return filterOptions ? filterOptions[filterValue as string] : filterValue
          })
          .with("inNumberRange", () => {
            if (!Array.isArray(filterValue)) return ""
            const [min, max, missing] = filterValue as [number, number, boolean]
            if (missing) return "Empty"

            const maskOpts = meta?.filterNumberMaskOptions ?? { mask: Number }
            const minLabel = IMask.pipe(min?.toString() ?? "", {
              thousandsSeparator: ",",
              radix: ".",
              ...maskOpts,
              lazy: false,
            })
            const maxLabel = IMask.pipe(max?.toString() ?? "", {
              thousandsSeparator: ",",
              radix: ".",
              ...maskOpts,
              lazy: false,
            })

            if (min === undefined && max === undefined) return ""
            if (min === undefined) return `≤ ${maxLabel}`
            if (max === undefined) return `≥ ${minLabel}`
            if (max === min) return `= ${minLabel}`
            return `${minLabel} - ${maxLabel}`
          })
          .with("boolean", () => {
            const value = filterValue as boolean
            if (value === undefined) return ""
            return value ? "Yes" : "No"
          })
          .with("inDateRange", () =>
            match(filterValue)
              .with([P._, P._, true], () => "Empty")
              .with(
                [P.nullish, P.nonNullable, false],
                ([_startDate, endDate, _isEmpty]) => `before ${endDate}`,
              )
              .with(
                [P.nonNullable, P.nullish, false],
                ([startDate, _endDate, _isEmpty]) => `after ${startDate}`,
              )
              .with([P.nonNullable, P.nonNullable, false], ([startDate, endDate, _]) =>
                startDate === endDate ? `on ${startDate}` : `${startDate} - ${endDate}`,
              )
              .otherwise(() => ""),
          )
          .otherwise(() => "")

        return (
          <DropdownMenu key={column.id}>
            <FilterPill
              id={table.getElementId(`${FILTER_PILL_ID_SUFFIX}-${column.id}`)}
              label={valueLabel ? `${label}: ${valueLabel}` : label}
              active={!!valueLabel}
              onRemove={valueLabel ? () => column.setFilterValue(undefined) : undefined}
              icon={meta?.filterPillIcon}
            />
            <DropdownMenuContent
              align="start"
              id={table.getElementId(`${FILTER_PILL_POPOVER_ID_SUFFIX}-${column.id}`)}
            >
              {match(column.columnDef.filterFn)
                .with("multiEnum", () => (
                  <MultiEnumFilter
                    col={column}
                    filterValue={filterValue as string[]}
                    table={table}
                  />
                ))
                .with("singleEnum", () => (
                  <SingleEnumFilter
                    col={column}
                    filterValue={filterValue as string}
                    table={table}
                  />
                ))
                .with("inNumberRange", () => (
                  <NumberRangeFilter
                    col={column}
                    filterValue={filterValue as [number, number, boolean]}
                  />
                ))
                .with("inDateRange", () => (
                  <DateRangeFilter
                    col={column}
                    filterValue={filterValue as [string, string, boolean]}
                  />
                ))
                .with("boolean", () => (
                  <BooleanFilter col={column} filterValue={filterValue as boolean} />
                ))
                .otherwise(() => null)}
            </DropdownMenuContent>
          </DropdownMenu>
        )
      })}
    </div>
  )
}

function shouldShowColumnFilterPill<T extends Record<string, any> = object>(
  col: Column<T>,
  columnFilters: ColumnFiltersState,
) {
  if (!(col.getCanFilter() || col.columnDef.enableColumnFilter)) return false
  if (col.columns.length > 0) return false
  if (col.columnDef.meta?.alwaysShowFilterPill) return true

  const filterValue = columnFilters.find((f) => f.id === col.id)?.value

  return match(col.columnDef.filterFn)
    .with("boolean", () => filterValue !== undefined)
    .with("multiEnum", () => Array.isArray(filterValue) && filterValue.length > 0)
    .with("singleEnum", () => filterValue !== undefined)
    .with(
      "inNumberRange",
      () =>
        Array.isArray(filterValue) &&
        filterValue.length === 3 &&
        filterValue.some((value) => !!value),
    )
    .with(
      "inDateRange",
      () =>
        Array.isArray(filterValue) &&
        filterValue.length === 3 &&
        filterValue.some((value) => !!value),
    )
    .otherwise(() => false)
}
